import classNames from 'classnames'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import React, {useContext, useEffect} from 'react'
import {Link} from 'react-router-dom'
import {useToggle} from 'react-use'
import {useFocused, useSelected, useSlateStatic} from 'slate-react'
import {AppEntities} from 'views/AppLocals/enums'
import {NullAsDefault, renderIf} from 'views/Shared'
import {removeCodiQuote, updateCodiQuote} from 'views/SlateEditor/functions'
import {withOutline} from 'views/SlateEditor/SlateEditor'
import SlateEditorContext from 'views/SlateEditor/SlateEditorContext'
import {ElementTypes} from 'views/SlateEditor/types'
import {SentenceNode} from '../SentenceNode'
import './Quote.css'
import {useCodiQuote} from './useCodiQuote'

const scope = ElementTypes.CODI_QUOTE

export const QuoteElement = ({ style, children, attributes }) => (
  <blockquote
    style={style}
    {...attributes}
    className="inline-flex">
    {children}
  </blockquote>
)

export const CodiQuoteWrapper = React.forwardRef(
  ({ children, ...props }, ref) => (
    <blockquote
      {...props}
      ref={ref}
      className={classNames(props.className, 'CodiQuote')}>
      {children}
    </blockquote>
  )
)

const CodiDocumentQuote = ({ url, title, content }) => (
  <span className="font-bold text-lg">
    {renderIf(
      content,
      <span className="italic">
        {content + (String(title ?? '').length > 0 ? ' - ' : '')}
      </span>
    )}
    {url ? (
      <Link
        to={url}
        target="_blank"
        className="text-primary">
        {title}
      </Link>
    ) : (
      <span className="text-color-100">{title}</span>
    )}
  </span>
)

const CodiDocumentRowQuote = ({
  url,
  rows,
  title,
  content,
  preview = false,
}) => {
  const paragraphs = Array.from(rows ?? [])
  return (
    <div className="flex flex-col text-color-100">
      <CodiDocumentQuote {...{ url, title, content }} />
      {renderIf(
        paragraphs.length,
        <div className="mt-2 font-normal">
          {paragraphs.map((text, index) => (
            <SentenceNode
              key={index}
              element={{ className: 'ListItem' }}>
              {text}
            </SentenceNode>
          ))}
        </div>
      )}
    </div>
  )
}

export function CodiQuoteRenderer({ pending, quote_type, ...props }) {
  if (!pending) {
    switch (quote_type) {
      case AppEntities.CODI_DOCUMENT:
        return <CodiDocumentQuote {...props} />
      case AppEntities.CODI_DOCUMENT_ROW:
        return <CodiDocumentRowQuote {...props} />
      default:
        break
    }
  }
  return null
}

export const CodiQuoteElement = ({
  children,
  element,
  attributes,
  ...props
}) => {
  const focused = useFocused()
  const selected = useSelected()
  const editor = useSlateStatic()

  const [isToggle, toggle] = useToggle()

  const { values, set = NullAsDefault } = useContext(SlateEditorContext)

  const { pending = false } = _.get(values, scope) ?? {}

  const { uid, quote_type } = element ?? {}

  const reset = () => {
    set(scope, undefined)
  }

  useEffect(() => {
    if (pending) {
      toggle(true)
    }
  }, [pending])

  const { modal } = useCodiQuote({
    open: isToggle,
    onCancel: () => {
      if (pending) {
        removeCodiQuote(editor, { match: (n) => n?.uid === uid })
      }
      toggle(false)
      reset()
    },
    onSubmit: (values) => {
      set(scope, { pending: false })
      updateCodiQuote(editor, {
        ...(values ?? {}),
        uid: undefined,
      })
      reset()
    },
    title: (
      <Translate>
        {(t) => (
          <div className="text-center font-bold text-color-000 uppercase tracking-wide">
            {t('Embed a section of a document')}
          </div>
        )}
      </Translate>
    ),
  })

  if (!quote_type || pending) {
    return (
      <span>
        {children}
        {modal}
      </span>
    )
  }

  return (
    <CodiQuoteWrapper
      style={{
        ...(props.style ?? {}),
        outline: withOutline({ selected, focused }),
        borderRadius: '0 0.375rem 0.375rem 0',
      }}
      contentEditable={false}
      {...attributes}>
      <CodiQuoteRenderer {...(element ?? {})} />
      {children}
    </CodiQuoteWrapper>
  )
}
